import React from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    IconButton,
    Tooltip,
    CircularProgress,
    Paper,
    Stack,
    Divider,
    Fade,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
} from "@mui/material";
import {
    LocationOn,
    ContentCopy,
    CheckCircle,
    Link as LinkIcon,
    Share,
    QrCode,
    Close,
    Download,
} from "@mui/icons-material";
import { Location } from "../../types";

interface EncodePanelProps {
    encodeLat: string;
    setEncodeLat: (val: string) => void;
    encodeLng: string;
    setEncodeLng: (val: string) => void;
    encodeResult: string;
    encodeError: string;
    onEncode: () => void;
    onSaveFavorite: () => void;
    invalidCoordinates: boolean;
    locationName: string;
    loadingLocation: boolean;
    selectedLocation: Location | null;
    copied: boolean;
    onCopy: (text: string) => void;
}

export const EncodePanel: React.FC<EncodePanelProps> = ({
    encodeLat,
    setEncodeLat,
    encodeLng,
    setEncodeLng,
    encodeResult,
    encodeError,
    onEncode,
    onSaveFavorite,
    invalidCoordinates,
    locationName,
    loadingLocation,
    selectedLocation,
    copied,
    onCopy,
}) => {
    const theme = useTheme();
    const [openQr, setOpenQr] = React.useState(false);

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'My DIGIPIN',
                    text: `Here is my DIGIPIN: ${encodeResult}`,
                    url: `https://www.google.com/maps?q=${selectedLocation?.lat},${selectedLocation?.lng}`,
                });
            } catch (error) {
                console.log('Error sharing:', error);
            }
        } else {
            // Fallback to copy
            onCopy(encodeResult);
        }
    };

    const downloadQr = async () => {
        try {
            const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(
                `https://www.google.com/maps?q=${selectedLocation?.lat},${selectedLocation?.lng}`
            )}`;

            const response = await fetch(qrUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `DIGIPIN-${encodeResult}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading QR code:", error);
        }
    };

    // Helper for QR code
    const QrImg = ({ value, size = 100 }: { value: string; size?: number }) => {
        const src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
            value
        )}`;
        return (
            <Box
                sx={{
                    p: 1,
                    bgcolor: "white",
                    borderRadius: 2,
                    display: "inline-block",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                }}
            >
                <img src={src} width={size} height={size} alt="QR code" style={{ display: "block" }} />
            </Box>
        );
    };

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ background: "linear-gradient(45deg, #3B82F6, #60A5FA)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    Encode Location
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Convert latitude and longitude coordinates into a unique DIGIPIN.
                </Typography>
            </Box>

            {/* Input Section - ALWAYS AT TOP */}
            <Paper sx={{ p: 3, borderRadius: 4 }}>
                <Stack spacing={2}>
                    {invalidCoordinates && (
                        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                            Invalid coordinates. Please select a location within India.
                        </Alert>
                    )}

                    {locationName && (
                        <Fade in>
                            <Alert
                                icon={<LocationOn fontSize="inherit" />}
                                severity="info"
                                variant="outlined"
                                sx={{
                                    borderRadius: 2,
                                    "& .MuiAlert-message": { width: "100%" },
                                }}
                            >
                                <Typography variant="subtitle2" fontWeight="bold">
                                    Selected Location
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                    {locationName}
                                </Typography>
                            </Alert>
                        </Fade>
                    )}

                    {loadingLocation && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1 }}>
                            <CircularProgress size={16} thickness={5} />
                            <Typography variant="caption" color="text.secondary">
                                Fetching address details...
                            </Typography>
                        </Stack>
                    )}

                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Latitude"
                            placeholder="28.6139"
                            value={encodeLat}
                            onChange={(e) => setEncodeLat(e.target.value)}
                            fullWidth
                            variant="outlined"
                            inputMode="decimal"
                            type="number"
                            InputProps={{ sx: { borderRadius: 3 } }}
                        />
                        <TextField
                            label="Longitude"
                            placeholder="77.2090"
                            value={encodeLng}
                            onChange={(e) => setEncodeLng(e.target.value)}
                            fullWidth
                            variant="outlined"
                            inputMode="decimal"
                            type="number"
                            InputProps={{ sx: { borderRadius: 3 } }}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
                        <Button
                            variant="contained"
                            onClick={onEncode}
                            fullWidth
                            size="large"
                            sx={{ borderRadius: 3, py: 1.5, fontSize: "1.1rem", textTransform: "none" }}
                        >
                            Generate DIGIPIN
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {/* Result Section - APPEARS BELOW */}
            {encodeResult && (
                <Fade in timeout={500}>
                    <Paper
                        elevation={6}
                        sx={{
                            p: 2,
                            borderRadius: 4,
                            background: "linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.1) 100%)",
                            border: "1px solid rgba(59, 130, 246, 0.3)",
                            boxShadow: "0 8px 32px rgba(59, 130, 246, 0.15)"
                        }}
                    >
                        <Stack spacing={2} alignItems="center">
                            <Box sx={{ textAlign: "center", width: "100%" }}>
                                <Typography variant="overline" color="primary" fontWeight="bold" sx={{ letterSpacing: 1.5 }}>
                                    GENERATED DIGIPIN
                                </Typography>

                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{ mt: 1, mb: 2 }}
                                >
                                    <Typography
                                        variant="h4"
                                        fontWeight="800"
                                        sx={{
                                            letterSpacing: 2,
                                            color: "#fff",
                                            textShadow: "0 0 20px rgba(59, 130, 246, 0.5)",
                                            fontFamily: "monospace"
                                        }}
                                    >
                                        {encodeResult}
                                    </Typography>

                                    {/* QR Preview Mini */}
                                    <Tooltip title="View QR Code">
                                        <Box
                                            onClick={() => setOpenQr(true)}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                bgcolor: "white",
                                                borderRadius: 1,
                                                p: 0.5,
                                                cursor: "pointer",
                                                "&:hover": { transform: "scale(1.1)" },
                                                transition: "all 0.2s"
                                            }}
                                        >
                                            <img
                                                src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(
                                                    `https://www.google.com/maps?q=${selectedLocation?.lat},${selectedLocation?.lng}`
                                                )}`}
                                                width="100%"
                                                height="100%"
                                                alt="QR"
                                                style={{ display: "block" }}
                                            />
                                        </Box>
                                    </Tooltip>
                                </Stack>

                                <Stack direction="row" spacing={1} justifyContent="center">
                                    <Button
                                        size="small"
                                        variant={copied ? "contained" : "outlined"}
                                        color={copied ? "success" : "primary"}
                                        startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                                        onClick={() => onCopy(encodeResult)}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        {copied ? "Copied" : "Copy"}
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Share />}
                                        onClick={handleShare}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        Share
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={onSaveFavorite}
                                        startIcon={<LocationOn />}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        Save
                                    </Button>
                                </Stack>
                            </Box>
                        </Stack>
                    </Paper>
                </Fade>
            )}

            <Dialog
                open={openQr}
                onClose={() => setOpenQr(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        bgcolor: "background.paper",
                        backgroundImage: "none",
                        border: "1px solid rgba(255,255,255,0.1)",
                        maxWidth: "xs",
                        width: "100%",
                        m: 2
                    }
                }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                        DIGIPIN QR Code
                    </Typography>
                    <IconButton onClick={() => setOpenQr(false)} size="small">
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4 }}>
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: "white",
                            borderRadius: 3,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                            mb: 3
                        }}
                    >
                        <img
                            id="qr-code-img"
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                                `https://www.google.com/maps?q=${selectedLocation?.lat},${selectedLocation?.lng}`
                            )}`}
                            width={200}
                            height={200}
                            alt="QR code"
                            style={{ display: "block" }}
                        />
                    </Box>
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: "primary.main", letterSpacing: 2 }}>
                        {encodeResult}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                        Scan to view location on maps
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0, justifyContent: "center" }}>
                    <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={downloadQr}
                        fullWidth
                        sx={{ borderRadius: 3 }}
                    >
                        Download QR
                    </Button>
                </DialogActions>
            </Dialog>

            {encodeError && (
                <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                    {encodeError}
                </Alert>
            )}
        </Stack>
    );
};
